import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useMemo } from 'react';
import { useSession, useSessionMutations } from '@/features/guided-financial-planning/context';
import { usePartnerLockedFundingSources } from './use-partner-locked-funding-sources';

import {
  selectClient,
  selectSession,
  selectSessionAdvisor,
  fetchClientByClientIdRequest,
} from '@client/model';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { SessionType } from '@shared';
import { guidedPlanningApi } from '@/features/guided-financial-planning/api';
import { type AssetTypeId } from '@/features/guided-financial-planning/config';
import type { AllocationOption } from '@/features/guided-financial-planning/components/partner-allocation-section';
import {
  splitFundingSourceFields,
  captureOriginalMonetaryValues,
} from '@/components/funding-sources-coordination/partner-funding-utils';

export const DEFAULT_CUSTOM_CLIENT_PERCENT = 50;

export type AllocationAction =
  | 'keep'
  | 'mirror'
  | 'split-50-50'
  | 'split-custom';

export interface SplitRatioConfig {
  clientRatio: number;
  partnerRatio: number;
  label: string;
  description: string;
}

interface PersistedSelection {
  action: string;
  partnerFundingSourceId?: string;
  fundingSourceGroupId?: string;
  originalValue: number;
  originalMonetaryValues?: Record<string, number>;
  customClientPercent?: number;
}

export const SPLIT_RATIO_CONFIGS: Record<Exclude<AllocationAction, 'keep' | 'split-custom'>, SplitRatioConfig> = {
  'mirror': {
    clientRatio: 1,
    partnerRatio: 1,
    label: 'Full Each',
    description: 'Both you and your partner each have the full amount available',
  },
  'split-50-50': {
    clientRatio: 0.5,
    partnerRatio: 0.5,
    label: '50 / 50',
    description: 'Split evenly between you and your partner',
  },
};

function allocationOptionToAction(option: AllocationOption): AllocationAction {
  if (option === 'custom') return 'split-custom';
  return option;
}

export function actionToAllocationOption(action: string): AllocationOption {
  if (action === 'split-custom') return 'custom';
  if (action === 'mirror' || action === 'split-50-50') return action;
  // Legacy split ratios (60/40, 70/30, 75/25) map to custom
  if (action.startsWith('split-')) return 'custom';
  return 'mirror';
}

function getRatios(
  action: AllocationAction,
  customClientPercent?: number,
): { clientRatio: number; partnerRatio: number } {
  if (action === 'split-custom') {
    const pct = Math.min(99, Math.max(1, customClientPercent ?? 50));
    return { clientRatio: pct / 100, partnerRatio: (100 - pct) / 100 };
  }
  if (action === 'keep') return { clientRatio: 1, partnerRatio: 0 };
  return SPLIT_RATIO_CONFIGS[action];
}

export function getActionLabel(action: string, customClientPercent?: number): string {
  if (action === 'split-custom' && customClientPercent != null) {
    return `${customClientPercent} / ${100 - customClientPercent}`;
  }
  if (action === 'keep') return 'Keep';
  const config = SPLIT_RATIO_CONFIGS[action as Exclude<AllocationAction, 'keep' | 'split-custom'>];
  return config?.label ?? action;
}

export interface CardAllocationState {
  enabled: boolean;
  option: AllocationOption;
  customClientPercent: number;
  originalValue?: number;
  originalMonetaryValues?: Record<string, number>;
  formValue?: number;
}

export function usePartnerAllocation() {
  const dispatch = useDispatch();
  const client = useSelector(selectClient);
  const { flags } = useFeatureFlags();

  const { sessionType } = useSelector(selectSession);
  const { advisorId } = useSelector(selectSessionAdvisor);

  const { session } = useSession();
  const { updateStepData } = useSessionMutations();

  const hasPartner = flags.partnerFundingSplit === true && !!client?.partnerClientId;

  const {
    partnerClient,
    partnerSharedFundingSourceIds,
    partnerSharedActionByFundingSourceId,
    partnerSharedOriginalValueByFundingSourceId,
  } = usePartnerLockedFundingSources();

  const isAdvisorSession =
    sessionType === SessionType.advisor || sessionType === SessionType.wizard;

  const savedSelections = useMemo(
    () =>
      (session?.stepData?.partnerAllocationSelections ?? {}) as Record<string, PersistedSelection>,
    [session?.stepData?.partnerAllocationSelections],
  );

  const getCardAllocationState = useCallback(
    (fundingSourceId: string): CardAllocationState => {
      const saved = savedSelections[fundingSourceId];
      if (!saved || saved.action === 'keep') {
        return { enabled: false, option: 'mirror', customClientPercent: DEFAULT_CUSTOM_CLIENT_PERCENT, originalValue: undefined };
      }
      return {
        enabled: true,
        option: actionToAllocationOption(saved.action),
        customClientPercent: saved.customClientPercent ?? DEFAULT_CUSTOM_CLIENT_PERCENT,
        originalValue: saved.originalValue,
        originalMonetaryValues: saved.originalMonetaryValues,
      };
    },
    [savedSelections],
  );

  const applyCardAllocation = useCallback(
    async (
      fundingSourceId: string,
      assetTypeId: AssetTypeId,
      selfFundingSource: SelfFundingSource,
      allocationState: CardAllocationState,
    ) => {
      if (!client || !partnerClient) return;

      const existingSelection = savedSelections[fundingSourceId];
      // When formValue is provided (re-edit), use it as the new total for recalculation
      const originalValue = allocationState.formValue
        ?? existingSelection?.originalValue
        ?? selfFundingSource.existingAssetsValue
        ?? 0;
      const updatedSelections: Record<string, PersistedSelection> = { ...savedSelections };

      // === UNLINK: user unchecked the allocation checkbox ===
      if (!allocationState.enabled) {
        if (existingSelection) {
          const wasSplit =
            existingSelection.action !== 'mirror' && existingSelection.action !== 'keep';
          const origMonetary = existingSelection.originalMonetaryValues;

          const clientRestore: Partial<SelfFundingSource> = {};
          const partnerRestore: Partial<SelfFundingSource> = {};

          if (wasSplit) {
            clientRestore.existingAssetsValue = originalValue;
            partnerRestore.existingAssetsValue = originalValue - (selfFundingSource.existingAssetsValue ?? 0);

            if (origMonetary) {
              if (origMonetary.monthlyContributionAmount != null) {
                clientRestore.monthlyContributionAmount = origMonetary.monthlyContributionAmount;
                partnerRestore.monthlyContributionAmount =
                  origMonetary.monthlyContributionAmount - (selfFundingSource.monthlyContributionAmount ?? 0);
              }
              if (origMonetary.monthlyIncome != null) {
                clientRestore.monthlyIncome = origMonetary.monthlyIncome;
                partnerRestore.monthlyIncome =
                  origMonetary.monthlyIncome - (selfFundingSource.monthlyIncome ?? 0);
              }
              if (origMonetary.drawdownAmount != null) {
                clientRestore.drawdownAmount = origMonetary.drawdownAmount;
                partnerRestore.drawdownAmount =
                  origMonetary.drawdownAmount - (selfFundingSource.drawdownAmount ?? 0);
              }
            }
          }

          await guidedPlanningApi.upsertSelfFunding({
            clientId: client.clientId,
            fundingSourceId,
            fundingSourceData: {
              ...selfFundingSource,
              ...clientRestore,
              partnerAction: null,
              fundingSourceGroupId: null,
              splitRatio: null,
            },
          });

          if (existingSelection.partnerFundingSourceId) {
            await guidedPlanningApi.upsertSelfFunding({
              clientId: partnerClient.clientId,
              fundingSourceId: existingSelection.partnerFundingSourceId,
              fundingSourceData: {
                ...selfFundingSource,
                ...(wasSplit ? partnerRestore : {}),
                hasSelfFunding: true,
                partnerAction: null,
                fundingSourceGroupId: null,
                splitRatio: null,
              },
            });
          }

          delete updatedSelections[fundingSourceId];
        }
        await updateStepData({ partnerAllocationSelections: updatedSelections });
        dispatch(fetchClientByClientIdRequest(client.clientId));
        return;
      }

      // === LINK / UPDATE: user enabled allocation or changed option ===
      const action = allocationOptionToAction(allocationState.option);
      const { clientRatio } = getRatios(action, allocationState.customClientPercent);
      const isMirror = action === 'mirror';

      const groupId =
        existingSelection?.fundingSourceGroupId ?? crypto.randomUUID();

      const clientSplitRatio = isMirror ? null : Math.round(clientRatio * 100);
      const partnerSplitRatio = isMirror ? null : (100 - Math.round(clientRatio * 100));

      // Build source with correct totals for splitting
      const sourceWithTotals: SelfFundingSource = {
        ...selfFundingSource,
        existingAssetsValue: originalValue,
      };

      const { clientOverrides, partnerOverrides } = isMirror
        ? { clientOverrides: { existingAssetsValue: originalValue }, partnerOverrides: { existingAssetsValue: originalValue } }
        : splitFundingSourceFields(sourceWithTotals, Math.round(clientRatio * 100));

      const originalMonetaryValues = captureOriginalMonetaryValues(sourceWithTotals);

      await guidedPlanningApi.upsertSelfFunding({
        clientId: client.clientId,
        fundingSourceId,
        fundingSourceData: {
          ...selfFundingSource,
          ...clientOverrides,
          partnerAction: 'mirrored',
          fundingSourceGroupId: groupId,
          splitRatio: clientSplitRatio,
        },
      });

      const existingPartnerSourceId = existingSelection?.partnerFundingSourceId ?? null;

      const partnerResponse = await guidedPlanningApi.upsertSelfFunding({
        clientId: partnerClient.clientId,
        fundingSourceId: existingPartnerSourceId,
        fundingSourceData: {
          ...selfFundingSource,
          ...partnerOverrides,
          hasSelfFunding: true,
          partnerAction: 'mirrored',
          fundingSourceGroupId: groupId,
          splitRatio: partnerSplitRatio,
          ...(isAdvisorSession && { addedByAdvisorId: advisorId }),
        },
      });

      updatedSelections[fundingSourceId] = {
        action,
        originalValue,
        originalMonetaryValues,
        partnerFundingSourceId: partnerResponse.fundingSourceId,
        fundingSourceGroupId: groupId,
        ...(action === 'split-custom' && {
          customClientPercent: allocationState.customClientPercent,
        }),
      };

      // Persist selections to session
      await updateStepData({ partnerAllocationSelections: updatedSelections });

      // Refresh Redux state
      dispatch(fetchClientByClientIdRequest(client.clientId));
    },
    [
      client,
      partnerClient,
      savedSelections,
      isAdvisorSession,
      advisorId,
      dispatch,
      updateStepData,
    ],
  );

  return {
    hasPartner,
    client,
    partnerClient,
    partnerSharedFundingSourceIds,
    partnerSharedActionByFundingSourceId,
    partnerSharedOriginalValueByFundingSourceId,
    getCardAllocationState,
    applyCardAllocation,
  };
}
